import {z} from 'zod';

import {validateCheckoutProfile} from './utils/validateCheckoutProfile';
import {validateCheckoutProfileRule} from './utils/validateCheckoutProfileRule';

export const CheckoutProfileTypeOfSaleSchema = z.enum(['DEFAULT', 'INTERNAL']);
export const CheckoutProfileSaleRegimeSchema = z.enum(['DOMESTIC', 'IN_EUROPE', 'OUT_OF_EUROPE']);
export const CheckoutProfilePaymentMethodSchema = z.enum([
  'CASH',
  'CARD',
  'BANK_TRANSFER',
  'CASH_ON_DELIVERY',
]);

export const CheckoutProfileDepositMethodSchema = z.enum(['CASH', 'CARD', 'BANK_TRANSFER']);
export const CheckoutProfileDocumentTypeSchema = z.enum([
  'INVOICE',
  'CORRECTIVE_INVOICE',
  'DEPOSIT_INVOICE',
  'TAX_DOCUMENT',
  'CORRECTIVE_TAX_DOCUMENT',
]);

export const CheckoutProfileSourceTypeSchema = z.enum(['service-order', 'direct-sale']);
export const CheckoutProfileTabSchema = z.enum(['GENERAL', 'PAYMENTS', 'DEPOSIT', 'DOCUMENTS']);
export const CheckoutProfileMissingCodeSchema = z
  .enum([
    'PAYMENT_METHOD_NONE',
    'DEFAULT_PAYMENT_METHOD_MISSING',
    'CASH_REGISTER_MISSING',
    'DEPOSIT_METHOD_NONE',
    'DEFAULT_DEPOSIT_METHOD_MISSING',
    'DEPOSIT_MATURITY_MISSING',
    'FOREIGN_CURRENCY_MISSING',
    'DOCUMENT_ROW_MISSING',
  ])
  .or(z.string());

export type SaleRegime = z.infer<typeof CheckoutProfileSaleRegimeSchema>;
export type CheckoutProfilePaymentMethod = z.infer<typeof CheckoutProfilePaymentMethodSchema>;
export type CheckoutProfileDocumentType = z.infer<typeof CheckoutProfileDocumentTypeSchema>;

const idSchema = z.string().uuid();
const daysSchema = z.number().int().min(0).nullish();
const bankAccountShapeSchema = z.object({
  iban: z.string().min(1).nullish(),
  number: z.string().min(1).nullish(),
});
const bankAccountSchema = bankAccountShapeSchema.refine((account) =>
  Boolean(account.iban || account.number)
);

const cashPaymentMethodSchema = z.object({
  method: z.literal('CASH'),
  isAutoInitiatePayment: z.boolean(),
  cashRegisterId: idSchema.nullish(),
});
const cardPaymentMethodSchema = z.object({
  method: z.literal('CARD'),
  isAutoInitiatePayment: z.boolean(),
});
const bankTransferPaymentMethodSchema = z.object({
  method: z.literal('BANK_TRANSFER'),
  isAutoInitiatePayment: z.boolean(),
  bankAccount: bankAccountSchema.nullish(),
  maturityDays: daysSchema,
  useCustomerMaturity: z.boolean().nullish(),
});
const cashOnDeliveryPaymentMethodSchema = z.object({
  method: z.literal('CASH_ON_DELIVERY'),
  isAutoInitiatePayment: z.boolean(),
});

const paymentMethodSchema = z.discriminatedUnion('method', [
  cashPaymentMethodSchema,
  cardPaymentMethodSchema,
  bankTransferPaymentMethodSchema,
  cashOnDeliveryPaymentMethodSchema,
]);

export const CheckoutProfileDocumentRowSchema = z.object({
  saleRegime: CheckoutProfileSaleRegimeSchema.nullable(),
  documentType: CheckoutProfileDocumentTypeSchema,
  paymentMethod: CheckoutProfilePaymentMethodSchema.nullable(),
  allowedSeriesIds: z.array(idSchema),
  defaultSeriesId: idSchema.nullish(),
  allowedTemplateIds: z.array(idSchema),
  defaultTemplateId: idSchema.nullish(),
  isOverrideAllowed: z.boolean(),
});

export type CheckoutProfileDocumentRow = z.infer<typeof CheckoutProfileDocumentRowSchema>;
export type CheckoutProfileDocumentCell = Pick<
  CheckoutProfileDocumentRow,
  'saleRegime' | 'documentType' | 'paymentMethod'
>;

const foreignCurrencySchema = z.object({
  isAllowed: z.boolean(),
  isDefault: z.boolean(),
  defaultCurrency: z
    .string()
    .regex(/^[A-Z]{3}$/)
    .nullish(),
});
const paymentsSchema = z.object({
  isMethodChangeAllowed: z.boolean(),
  defaultMethod: CheckoutProfilePaymentMethodSchema.nullish(),
  methods: z
    .array(paymentMethodSchema)
    .refine((methods) => new Set(methods.map((payment) => payment.method)).size === methods.length),
});
const depositSchema = z.object({
  isAllowed: z.boolean(),
  isMethodChangeAllowed: z.boolean().nullish(),
  methods: z.array(CheckoutProfileDepositMethodSchema).optional(),
  defaultMethod: CheckoutProfileDepositMethodSchema.nullish(),
  maturityDays: daysSchema,
});

// Keep the inferred aggregate independent from refinements that consume it.
const checkoutProfileWriteBodySchema = z.object({
  name: z.string().trim().min(1).max(100),
  branchId: idSchema,
  typeOfSale: CheckoutProfileTypeOfSaleSchema,
  isActive: z.boolean(),
  saleRegimes: z.object({inEurope: z.boolean(), outOfEurope: z.boolean()}).nullish(),
  foreignCurrency: foreignCurrencySchema.nullish(),
  payments: paymentsSchema.nullish(),
  deposit: depositSchema.nullish(),
  documentTypes: z
    .object({
      correctiveInvoice: z.boolean().nullish(),
      correctiveTaxDocument: z.boolean().nullish(),
    })
    .nullish(),
  documents: z.array(CheckoutProfileDocumentRowSchema),
});

export type CheckoutProfileWrite = z.infer<typeof checkoutProfileWriteBodySchema>;
export const CheckoutProfileWriteSchema =
  checkoutProfileWriteBodySchema.superRefine(validateCheckoutProfile);

// The profile editor keeps the values of sections it hides (a disabled deposit, the external
// settings of an internal sale) in its state. This shape accepts those values without the field
// rules, so normalizeCheckoutProfileDraft can strip the hidden sections before
// CheckoutProfileWriteSchema validates the request. Document rows pass form-only flags through.
export const CheckoutProfileDraftSchema = checkoutProfileWriteBodySchema.extend({
  name: z.string(),
  branchId: z.string(),
  foreignCurrency: foreignCurrencySchema.extend({defaultCurrency: z.string().nullish()}).nullish(),
  payments: paymentsSchema
    .extend({
      methods: z.array(
        z.discriminatedUnion('method', [
          cashPaymentMethodSchema,
          cardPaymentMethodSchema,
          bankTransferPaymentMethodSchema.extend({
            bankAccount: bankAccountShapeSchema.nullish(),
            maturityDays: z.number().nullish(),
          }),
          cashOnDeliveryPaymentMethodSchema,
        ])
      ),
    })
    .nullish(),
  deposit: depositSchema.extend({maturityDays: z.number().nullish()}).nullish(),
  documents: z.array(CheckoutProfileDocumentRowSchema.passthrough()),
});

export type CheckoutProfileDraft = z.infer<typeof CheckoutProfileDraftSchema>;

export const CheckoutProfileMissingSchema = z.object({
  tab: CheckoutProfileTabSchema,
  code: CheckoutProfileMissingCodeSchema,
  saleRegime: CheckoutProfileSaleRegimeSchema.nullish(),
  documentType: CheckoutProfileDocumentTypeSchema.nullish(),
  paymentMethod: CheckoutProfilePaymentMethodSchema.nullish(),
});

export type CheckoutProfileMissing = z.infer<typeof CheckoutProfileMissingSchema>;

const auditShape = {
  createdAt: z.string(),
  createdBy: z.string(),
  updatedAt: z.string(),
  updatedBy: z.string(),
};

export const CheckoutProfileSchema = checkoutProfileWriteBodySchema
  .extend({
    id: idSchema,
    baseCurrency: z.string().regex(/^[A-Z]{3}$/),
    isComplete: z.boolean(),
    missing: z.array(CheckoutProfileMissingSchema),
    version: z.string().min(1),
    activeDocumentTypes: z.array(CheckoutProfileDocumentTypeSchema),
    ...auditShape,
  })
  .superRefine(validateCheckoutProfile);

export type CheckoutProfile = z.infer<typeof CheckoutProfileSchema>;

export const CheckoutProfilePickerItemSchema = checkoutProfileWriteBodySchema
  .pick({name: true, branchId: true, typeOfSale: true, isActive: true})
  .extend({id: idSchema, isComplete: z.boolean()});

export type CheckoutProfilePickerItem = z.infer<typeof CheckoutProfilePickerItemSchema>;

export const CheckoutProfileIdArgSchema = z.object({checkoutProfileId: idSchema});
export const CheckoutProfileIdsArgSchema = z.object({checkoutProfileIds: z.array(idSchema)});
export const CheckoutProfileListArgSchema = z.object({
  branchId: idSchema.optional(),
  typeOfSale: CheckoutProfileTypeOfSaleSchema.optional(),
  isActive: z.boolean().optional(),
});

export const CreateCheckoutProfileArgSchema = z.object({body: CheckoutProfileWriteSchema});
export const UpdateCheckoutProfileArgSchema = CheckoutProfileIdArgSchema.extend({
  body: checkoutProfileWriteBodySchema
    .extend({version: z.string().min(1)})
    .superRefine(validateCheckoutProfile),
});

export type CheckoutProfileIdArg = z.infer<typeof CheckoutProfileIdArgSchema>;
export type CheckoutProfileIdsArg = z.infer<typeof CheckoutProfileIdsArgSchema>;
export type CheckoutProfileListArg = z.infer<typeof CheckoutProfileListArgSchema>;
export type CreateCheckoutProfileArg = z.infer<typeof CreateCheckoutProfileArgSchema>;
export type UpdateCheckoutProfileArg = z.infer<typeof UpdateCheckoutProfileArgSchema>;

const conditionIdsSchema = z.array(idSchema).min(1, 'CONDITION_EMPTY').nullish();
export const CheckoutProfileRuleConditionsSchema = z.object({
  serviceCaseTypeIds: conditionIdsSchema,
  serviceOrderTypeIds: conditionIdsSchema,
  serviceOrderVariantIds: conditionIdsSchema,
  authorizationProfileIds: conditionIdsSchema,
  directSaleTypeIds: conditionIdsSchema,
  directSaleVariantIds: conditionIdsSchema,
});

export type CheckoutProfileRuleConditions = z.infer<typeof CheckoutProfileRuleConditionsSchema>;

const checkoutProfileRuleWriteBodySchema = z.object({
  name: z.string().trim().min(1).max(100),
  branchId: idSchema,
  checkoutProfileId: idSchema,
  sourceType: CheckoutProfileSourceTypeSchema,
  isActive: z.boolean(),
  priority: z.number().int().min(0),
  conditions: CheckoutProfileRuleConditionsSchema,
});

export type CheckoutProfileRuleWrite = z.infer<typeof checkoutProfileRuleWriteBodySchema>;
export const CheckoutProfileRuleWriteSchema = checkoutProfileRuleWriteBodySchema.superRefine(
  validateCheckoutProfileRule
);

export const CheckoutProfileRuleSchema = checkoutProfileRuleWriteBodySchema
  .extend({
    id: idSchema,
    checkoutProfile: CheckoutProfilePickerItemSchema.pick({
      id: true,
      name: true,
      typeOfSale: true,
      isActive: true,
    }),
    ...auditShape,
  })
  .superRefine(validateCheckoutProfileRule);

export type CheckoutProfileRule = z.infer<typeof CheckoutProfileRuleSchema>;

export const CheckoutProfileRuleConflictSchema = z.object({
  id: idSchema,
  name: z.string(),
  priority: z.number().int().min(0),
});

// A preview can select the current rule before it has been saved and assigned an ID.
const winningRuleSchema = z.object({
  id: idSchema.nullish(),
  name: z.string(),
  isThisRule: z.boolean(),
});

export const CheckoutProfileMatchingVariantSchema = z.object({
  variantId: idSchema,
  variantName: z.string(),
  serviceCaseTypeName: z.string().nullish(),
  authorizationProfileName: z.string().nullish(),
  typeName: z.string(),
  winningRuleDefault: winningRuleSchema.nullish().transform((winner) => winner ?? null),
  winningRuleInternal: winningRuleSchema.nullish().transform((winner) => winner ?? null),
});

export const CheckoutProfileRulePreviewSchema = z.preprocess(
  (response) => response ?? {},
  z.object({
    conflicts: z
      .array(CheckoutProfileRuleConflictSchema)
      .nullish()
      .transform((items) => items ?? []),
    matchingVariants: z
      .array(CheckoutProfileMatchingVariantSchema)
      .nullish()
      .transform((items) => items ?? []),
  })
);

export type CheckoutProfileRulePreview = z.infer<typeof CheckoutProfileRulePreviewSchema>;

export const CheckoutProfileRuleIdArgSchema = z.object({checkoutProfileRuleId: idSchema});
export const CheckoutProfileRuleIdsArgSchema = z.object({
  checkoutProfileRuleIds: z.array(idSchema),
});
export const CreateCheckoutProfileRuleArgSchema = z.object({body: CheckoutProfileRuleWriteSchema});
export const UpdateCheckoutProfileRuleArgSchema = CheckoutProfileRuleIdArgSchema.extend({
  body: CheckoutProfileRuleWriteSchema,
});

export const SwapCheckoutProfileRulePriorityArgSchema = z.object({
  checkoutProfileRuleIds: z
    .tuple([idSchema, idSchema])
    .refine(([first, second]) => first !== second),
});

export const PreviewCheckoutProfileRuleArgSchema = z.object({
  body: checkoutProfileRuleWriteBodySchema
    .extend({id: idSchema.optional(), name: z.string().trim().max(100).optional().default('')})
    .superRefine(validateCheckoutProfileRule),
});

export type CheckoutProfileRuleIdArg = z.infer<typeof CheckoutProfileRuleIdArgSchema>;
export type CheckoutProfileRuleIdsArg = z.infer<typeof CheckoutProfileRuleIdsArgSchema>;
export type CreateCheckoutProfileRuleArg = z.infer<typeof CreateCheckoutProfileRuleArgSchema>;
export type UpdateCheckoutProfileRuleArg = z.infer<typeof UpdateCheckoutProfileRuleArgSchema>;
export type SwapCheckoutProfileRulePriorityArg = z.infer<
  typeof SwapCheckoutProfileRulePriorityArgSchema
>;

export type PreviewCheckoutProfileRuleArg = z.input<typeof PreviewCheckoutProfileRuleArgSchema>;

export const CheckoutProfileErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
  path: z.string(),
  // A rejected row can itself contain invalid identity values; retain the error without them.
  saleRegime: CheckoutProfileSaleRegimeSchema.nullish().catch(undefined),
  documentType: CheckoutProfileDocumentTypeSchema.optional().catch(undefined),
  paymentMethod: CheckoutProfilePaymentMethodSchema.nullish().catch(undefined),
  checkoutProfileId: idSchema.optional(),
  missing: z.array(CheckoutProfileMissingSchema).optional(),
  rules: z.array(CheckoutProfileRuleConflictSchema).optional(),
});

export type CheckoutProfileError = z.infer<typeof CheckoutProfileErrorSchema>;
export const CheckoutProfileErrorResponseSchema = z.object({
  errors: z.array(CheckoutProfileErrorSchema),
});
